"use client";

import type { ArchiveHookStepResult } from "@taskin-code/contracts";

import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import type { ArchiveHookRun } from "~/archiveHookStore";
import { CommandOutputBlock } from "./CommandOutputBlock";

export interface ArchiveHookOutputDialogProps {
  readonly open: boolean;
  readonly run: ArchiveHookRun | null;
  readonly onClose: () => void;
}

export function ArchiveHookOutputDialog({ open, run, onClose }: ArchiveHookOutputDialogProps) {
  const title =
    run?.phase === "error"
      ? `Archive hook falló${
          run.failedStepIndex !== null ? ` (paso ${run.failedStepIndex + 1})` : ""
        }`
      : "Archive hook — output";

  const description = run
    ? `${run.steps.length}/${run.totalSteps} paso(s) ejecutado(s) en ${run.worktreePath}`
    : "";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mx-6 flex max-h-[60vh] flex-col gap-3 overflow-auto">
          {run?.steps.length ? (
            run.steps.map((step) => <ArchiveHookStepBlock key={step.stepIndex} step={step} />)
          ) : (
            <p className="text-sm text-muted-foreground">Sin output todavía.</p>
          )}
        </div>
        <div className="flex flex-row-reverse gap-2 p-6 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

function ArchiveHookStepBlock({ step }: { readonly step: ArchiveHookStepResult }) {
  const statusLabel = step.timedOut
    ? "timeout"
    : step.exitCode === 0
      ? "ok"
      : `exit ${step.exitCode ?? "null"}`;
  return (
    <CommandOutputBlock
      command={step.command}
      prefix={`#${step.stepIndex + 1}`}
      statusLabel={statusLabel}
      durationMs={step.durationMs}
      stdout={step.stdout}
      stderr={step.stderr}
      stdoutTruncated={step.stdoutTruncated}
      stderrTruncated={step.stderrTruncated}
    />
  );
}
